/**
 * Write & send › who sends and who takes the call (Sofia + Luisa, Oct 26 2026).
 *
 * TA writes from colleagues' LinkedIn profiles too, and either that colleague or the recruiter who
 * sourced takes the first call. People Space cannot see which profile is signed in, so the step
 * asks once per visit (a dialog) and always shows the answer in one line:
 * "Sending as Luisa · Call with Sofia · Change". Send as = the signature; Call with = whose
 * screening link the InMail books. Saving puts open drafts together again for it (server side).
 */
import { useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate, SendAsView } from '../../types/taOutreach';
import { SenderFacts } from './SenderFacts';

const NEW = '__new__';
/** Asked once per browser session; the choice itself is saved on the server. */
const ASKED_KEY = 'outreach-send-as-asked';
const errText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);
const first = (name: string) => name.split(' ')[0] || name;

function askedThisSession() {
  try {
    return sessionStorage.getItem(ASKED_KEY) === '1';
  } catch {
    return false;
  }
}
function markAsked() {
  try {
    sessionStorage.setItem(ASKED_KEY, '1');
  } catch {
    /* private window: ask again next time */
  }
}

const selectCls = 'h-9 w-full rounded-md border border-solid border-[#D6D6D6] bg-white px-2 text-sm';

/** After a save: how many open drafts were put together again, and how many need the recruiter. */
function savedToast({ updated, needsYou }: { updated: number; needsYou: number }) {
  if (!updated && !needsYou) return;
  showToast({
    status: needsYou ? 'warning' : 'success',
    description: `${updated} open draft${updated === 1 ? '' : 's'} updated.${needsYou ? ` ${needsYou} edited by hand: open them and press “Update”.` : ''}`,
  });
}

/**
 * `stale`: the viewer's OWN drafts for another sender or caller, one per person (Oct 28 2026) —
 * a colleague's draft is not theirs to update and is signed for them only when they send it.
 */
export function SendAsBar({ stale, onSaved }: { stale: OutreachCandidate[]; onSaved: () => void }) {
  const [view, setView] = useState<SendAsView | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    void api
      .fetchSendAs()
      .then((v) => {
        if (!alive) return;
        setView(v);
        if (!askedThisSession()) setOpen(true);
      })
      .catch((err) => showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not load who sends.') }));
    return () => {
      alive = false;
    };
  }, []);

  if (!view) return null;
  const nameOf = (email: string) => view.senders.find((p) => p.email === email)?.name || view.interviewers.find((p) => p.email === email)?.name || email;
  const sender = first(nameOf(view.choice.sendAs));
  const caller = first(nameOf(view.choice.interviewer));
  const same = view.choice.sendAs === view.choice.interviewer;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-solid border-[#DBEBFC] bg-[#F3F8FE] px-[18px] py-[11px] text-[13px]" data-testid="send-as-bar">
        <span>
          Sending as <b>{sender}</b> · Call with <b>{caller}</b>
          {same ? '' : ` (the InMail books with ${caller})`}
        </span>
        <span className="text-xs text-[#707070]">Be signed in to LinkedIn as {sender}.</span>
        <span className="flex-1" />
        <button type="button" className="text-xs font-semibold text-[#0072EA] underline" onClick={() => setOpen(true)}>
          Change
        </button>
      </div>
      {open && (
        <SendAsDialog
          view={view}
          onClose={() => {
            markAsked();
            setOpen(false);
          }}
          onSaved={(next, r) => {
            markAsked();
            setView(next);
            setOpen(false);
            savedToast(r);
            onSaved();
          }}
        />
      )}
      {stale.length > 0 && (
        <p className="m-0 rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-[18px] py-[11px] text-[13px] text-[#7A4B00]">
          <b>{stale.length}</b> of your drafts {stale.length === 1 ? 'was' : 'were'} written for another sender or caller: {stale.map((c) => c.name).join(', ')}.
          “Update all drafts” below puts {stale.length === 1 ? 'it' : 'them'} together for you — not sent until then.
        </p>
      )}
    </>
  );
}

function SendAsDialog({
  view,
  onClose,
  onSaved,
}: {
  view: SendAsView;
  onClose: () => void;
  onSaved: (view: SendAsView, r: { updated: number; needsYou: number }) => void;
}) {
  const [sendAs, setSendAs] = useState(view.choice.sendAs);
  const [interviewer, setInterviewer] = useState(view.choice.interviewer);
  const [useCommonGround, setUseCommonGround] = useState(view.choice.useCommonGround !== false);
  const [reading, setReading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [person, setPerson] = useState({ firstName: '', lastName: '', email: '', linkedinUrl: '' });
  const [busy, setBusy] = useState(false);
  const [senders, setSenders] = useState(view.senders);
  const [interviewers, setInterviewers] = useState(view.interviewers);
  // "Create screening link" shows the settings first; nothing is made until "Create" (Oct 26 2026).
  const [preview, setPreview] = useState<api.ScreeningLinkPreview | null>(null);
  const callee = interviewers.find((p) => p.email === interviewer);

  async function add() {
    setBusy(true);
    try {
      const { sender } = await api.addSender(person);
      setSenders((list) => [...list.filter((p) => p.email !== sender.email), sender]);
      setInterviewers((list) => (list.some((p) => p.email === sender.email) ? list : [...list, { ...sender, hasLink: false }]));
      setSendAs(sender.email);
      setInterviewer(sender.email);
      setAdding(false);
      setPerson({ firstName: '', lastName: '', email: '', linkedinUrl: '' });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not add them.') });
    } finally {
      setBusy(false);
    }
  }

  async function showSettings(email: string) {
    setBusy(true);
    try {
      setPreview(await api.previewScreeningLink(email));
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not read the settings.') });
    } finally {
      setBusy(false);
    }
  }

  async function createLink(email: string) {
    setBusy(true);
    try {
      await api.createScreeningLink(email);
      setPreview(null);
      setInterviewers((list) => list.map((p) => (p.email === email ? { ...p, hasLink: true } : p)));
      showToast({ status: 'success', description: 'Screening link ready. Change it any time on Interview Links.' });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not make the screening link.') });
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    try {
      const r = await api.saveSendAs(sendAs, interviewer, useCommonGround);
      onSaved({ ...view, choice: r.choice, senders, interviewers }, { updated: r.updated, needsYou: r.needsYou.length });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not save.') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onOpenChange={(next: boolean) => !next && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,28rem)] gap-3 rounded p-5">
        <ModalHeader className="p-0">
          <ModalTitle className="!mt-0 !text-lg !font-semibold">Who sends these InMails?</ModalTitle>
        </ModalHeader>
        <label className="block space-y-1 text-[13px]">
          <span className="font-semibold">Send as</span>
          <span className="block text-xs text-[#707070]">The LinkedIn profile you are signed in with. It signs the InMail and the follow-up.</span>
          <select
            aria-label="Send as"
            className={selectCls}
            value={adding ? NEW : sendAs}
            onChange={(e) => {
              if (e.target.value === NEW) return setAdding(true);
              setAdding(false);
              // Most often the profile's owner takes the call too.
              if (interviewer === sendAs) setInterviewer(e.target.value);
              setSendAs(e.target.value);
            }}
          >
            {senders.map((p) => (
              <option key={p.email} value={p.email}>
                {p.email === view.me ? `${p.name} (you)` : p.name}
              </option>
            ))}
            <option value={NEW}>Someone new…</option>
          </select>
        </label>
        {adding && (
          <div className="space-y-2 rounded-md border border-solid border-[#E9EAEC] p-3">
            <div className="grid grid-cols-2 gap-2">
              <Input label="First name" value={person.firstName} onChange={(e) => setPerson({ ...person, firstName: (e.target as HTMLInputElement).value })} />
              <Input label="Last name" value={person.lastName} onChange={(e) => setPerson({ ...person, lastName: (e.target as HTMLInputElement).value })} />
            </div>
            <Input label="FINN email" value={person.email} onChange={(e) => setPerson({ ...person, email: (e.target as HTMLInputElement).value })} />
            <Input label="LinkedIn profile link" value={person.linkedinUrl} onChange={(e) => setPerson({ ...person, linkedinUrl: (e.target as HTMLInputElement).value })} />
            <Button size="sm" variant="outline" className="rounded-md" disabled={busy || !person.firstName.trim() || !person.email.trim()} onClick={() => void add()}>
              Add
            </Button>
          </div>
        )}
        {/* Common ground (PR 7, Oct 27 2026): what the sender and the candidate share. */}
        <Checkbox
          id="send-as-common-ground"
          checked={useCommonGround}
          onCheckedChange={(v) => setUseCommonGround(v === true)}
          label="Use common ground"
          labelClassName="text-[13px] font-semibold"
          subtitle="When the candidate’s profile shares one thing with the sender’s (employer, university …), the InMail opens with it. New drafts only."
          subtitleClassName="text-xs text-[#707070]"
        />
        {useCommonGround && !adding && <SenderFacts
            key={sendAs}
            email={sendAs}
            name={senders.find((p) => p.email === sendAs)?.name || sendAs}
            linkedinUrl={senders.find((p) => p.email === sendAs)?.linkedinUrl || null}
            onReading={setReading}
          />}
        <label className="block space-y-1 text-[13px]">
          <span className="font-semibold">Call with</span>
          <span className="block text-xs text-[#707070]">Who takes the first call. The InMail links to their screening link.</span>
          <select aria-label="Call with" className={selectCls} value={interviewer} onChange={(e) => setInterviewer(e.target.value)}>
            {interviewers.map((p) => (
              <option key={p.email} value={p.email}>
                {p.email === view.me ? `${p.name} (you)` : p.name}
                {p.hasLink ? '' : ' — no screening link'}
              </option>
            ))}
          </select>
        </label>
        {callee && !callee.hasLink && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-3 py-2 text-xs text-[#7A4B00]">
            <span className="flex-1">
              {callee.email === view.me ? 'You have' : `${first(callee.name)} has`} no open screening link, so the InMail has no booking link.
            </span>
            {!preview && (
              <Button size="sm" variant="outline" className="rounded-md" disabled={busy} onClick={() => void showSettings(callee.email)}>
                Create screening link
              </Button>
            )}
            {preview && preview.email === callee.email && (
              <div className="w-full space-y-2 border-t border-solid border-[#F5C26B] pt-2 text-[#1A1A1A]" data-testid="screening-preview">
                <p className="m-0 font-semibold">
                  {preview.state === 'closed' ? 'Reopen' : 'New'} screening link for {first(callee.name)}
                </p>
                <ul className="m-0 list-disc space-y-0.5 pl-4">
                  <li>{preview.minutes}-minute calls</li>
                  <li>
                    {preview.hours.join(', ') || 'No hours set'} ({preview.timezone})
                  </li>
                  <li>
                    Bookable from {preview.minNoticeHours} h ahead, up to {preview.offeredRangeDays} days out
                  </li>
                  <li>Bookings go into {callee.email === view.me ? 'your' : `${first(callee.name)}’s`} calendar</li>
                </ul>
                <p className="m-0 text-[#707070]">These can be changed later on Interview Links.</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="primary" className="rounded-md" disabled={busy} onClick={() => void createLink(callee.email)}>
                    {preview.state === 'closed' ? 'Reopen' : 'Create'}
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-md" disabled={busy} onClick={() => setPreview(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        <p className="m-0 text-xs text-[#707070]">
          {sendAs === interviewer
            ? 'The InMail says: “book a short call with me”.'
            : `The InMail says: “book a short call with ${first(interviewers.find((p) => p.email === interviewer)?.name || '')}”.`}{' '}
          Open drafts are updated.
        </p>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" className="rounded-md" onClick={onClose} disabled={busy}>
            Keep as it is
          </Button>
          <Button size="sm" variant="primary" className="rounded-md" onClick={() => void save()} disabled={busy || adding || reading}>
            Save
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
